import type { FC } from "react"
import React, { useEffect, useState } from "react"

import { motion } from "framer-motion"
import { atom, useRecoilValue } from "recoil"
import styled from "styled-components"

import { useDesignRequest } from "~/containers/Proposal/state/design-request/use-design-request"
import { designIdState } from "~/containers/Proposal/state/designs/design-state"
import { useFetchDesignState } from "~/containers/Proposal/state/designs/use-design"
import { proposalRemoteState } from "~/containers/Proposal/state/proposal/proposal-state"

import { Card } from "./Card"
import { FloatingBar } from "./FloatingBar/FloatingBar"
import { Header } from "./Header"
import { Placeholder } from "./Placeholder"
import { findImageState } from "../state/imageElements"
import { imageSourceState } from "../state/view"

export const showDesignSwitcherState = atom({
  key: `showDesignSwitcher`,
  default: false,
})

const Wrapper = styled(motion.div)`
  position: absolute;
  background: white;
  width: 500px;
  height: 100vh;
  right: 0;
  top: 0;
  z-index: 999999;
  isolation: isolate;
`

const CardsWrapper = styled.div`
  height: calc(100vh - 75px);
  width: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  position: relative;
  padding: var(--space-xs);
  gap: var(--space-xs);
`

const DesignSwitcher_Internal: FC = () => {
  const proposal = useRecoilValue(proposalRemoteState)
  const designsSorted = [...proposal.designs].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )

  const { activateDesign } = useDesignRequest()
  const designId = useRecoilValue(designIdState)
  const [currentDesignId, setCurrentDesignId] = useState<string>(designId)
  const [previewDesignId, setPreviewDesignId] = useState<string>(designId)
  const [disabled, setDisabled] = useState(false)
  useFetchDesignState({ designId: previewDesignId })

  const handleActivate = async (designId: string) => {
    setPreviewDesignId(designId)
    try {
      await activateDesign(designId)
      setCurrentDesignId(designId)
    } catch {
      // useDesignRequest reports the rejection (for example the Aurora Design Source Lock);
      // the Active Design is unchanged.
    }
  }

  const [id] = useRecoilValue(imageSourceState)
  const image = useRecoilValue(findImageState(id))

  const isCurrent = previewDesignId === currentDesignId
  const openDesign = designsSorted.find((d) => d.id === previewDesignId)
  const proposalId = proposal.id

  useEffect(() => {
    if (proposalId) {
      const mostRecentDesign = [...proposal.designs].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )[0]
      const disableDuplicate =
        mostRecentDesign &&
        new Date(mostRecentDesign.createdAt).getTime() >= Date.now() - 1000 * 60
      setDisabled(disableDuplicate)
      const timeoutId = setTimeout(() => {
        setDisabled(false)
      }, 1000 * 60)

      return () => {
        clearTimeout(timeoutId)
        setDisabled(false)
      }
    }
  }, [proposal.designs, proposalId])

  return (
    <>
      <Wrapper
        initial={{
          x: `100%`,
        }}
        animate={{
          x: 0,
        }}
        exit={{
          x: `100%`,
          transition: {
            duration: 0.3,
          },
        }}
        transition={{
          duration: 0.3,
        }}
      >
        <Header
          resetDesign={() => setPreviewDesignId(currentDesignId)}
          disabled={disabled}
        />
        <CardsWrapper>
          {designsSorted.map((design, index) => {
            return (
              <Card
                key={design.id}
                design={design}
                index={proposal.designs.length - index}
                image={image?.filename ?? ``}
                isCurrent={design.id === currentDesignId}
                isPreview={design.id === previewDesignId}
                previewDesign={(id: string) => setPreviewDesignId(id)}
              />
            )
          })}
          {proposal.designs.length <= 1 && <Placeholder />}
        </CardsWrapper>
      </Wrapper>
      <FloatingBar
        isCurrent={isCurrent}
        activateDesign={() => handleActivate(previewDesignId)}
        designRevision={openDesign}
        disabled={disabled}
      />
    </>
  )
}

export const DesignSwitcher = React.memo(DesignSwitcher_Internal)
